import {
  GreyBoxLoader,
  GreyCircleLoader,
  IconLogo,
  Loader,
} from '@xarvio/elements';
import { PageLayoutAuth } from '@xarvio/frontend-core-components/components/PageLayoutAuth/PageLayoutAuth';
import { getAuthBackgroundSrc } from '@xarvio/frontend-core-components/components/PageLayoutAuth/getAuthBackgroundSrc';
import { useIsFeatureEnabled } from '@xarvio/frontend-core-state/lib/featureFlags/useIsFeatureEnabled';
import { useWhitelabel } from '@xarvio/frontend-core-state/lib/auth/hooks/useWhitelabel/useWhitelabel';
import { withAppBasePath } from '@xarvio/frontend-core-platform/lib/basePath';
import {
  isBarillaDomain,
  isPeroniDomain,
} from '@xarvio/frontend-core-state/lib/auth/utils/whitelabelDomain';
import { Overlay } from '@xarvio/utils';
import { useLocation } from 'react-router-dom';
import { IntlProvider } from 'react-intl';

import { AuthRoutes } from '@xarvio/frontend-core-router/lib/AuthRoutes';
import styles from './AuthRoutesLoading.module.scss';
import { getAuthRoutePath } from './authRouteUtils';

const AUTH_LOADING_MESSAGES = {
  'general.planSmarterGrowBetter': 'Plan Smarter. Grow Better.',
};
const ignoreIntlError = () => undefined;

enum AuthLoadingVariant {
  Login = 'login',
  Registration = 'registration',
  PasswordReset = 'passwordReset',
  SmartSprayerLanding = 'smartSprayerLanding',
  AutoLogin = 'autoLogin',
  EmailVerified = 'emailVerified',
  AcceptTerms = 'acceptTerms',
}

const AUTH_LOADING_VARIANT_BY_ROUTE: Record<AuthRoutes, AuthLoadingVariant> = {
  [AuthRoutes.LOGIN]: AuthLoadingVariant.Login,
  [AuthRoutes.REGISTRATION]: AuthLoadingVariant.Registration,
  [AuthRoutes.SMART_SPRAYER_REGISTRATION]: AuthLoadingVariant.Registration,
  [AuthRoutes.FORGOT_PASSWORD]: AuthLoadingVariant.PasswordReset,
  [AuthRoutes.RESET_PASSWORD]: AuthLoadingVariant.PasswordReset,
  [AuthRoutes.SMART_SPRAYER_LANDING]: AuthLoadingVariant.SmartSprayerLanding,
  [AuthRoutes.AUTO_LOGIN]: AuthLoadingVariant.AutoLogin,
  [AuthRoutes.EMAIL_VERIFIED]: AuthLoadingVariant.EmailVerified,
  [AuthRoutes.ACCEPT_TC]: AuthLoadingVariant.AcceptTerms,
};

const getAuthLoadingVariant = (pathname: string): AuthLoadingVariant => {
  const authRoute = getAuthRoutePath(pathname);
  return authRoute
    ? AUTH_LOADING_VARIANT_BY_ROUTE[authRoute]
    : AuthLoadingVariant.Login;
};

interface AuthLoadingLoginProps {
  showRegistration: boolean;
}

const AuthLoadingLogin = ({ showRegistration }: AuthLoadingLoginProps) => (
  <div
    className={`${styles.card} ${styles.loginCard} ${
      showRegistration ? styles.loginCardWithRegistration : ''
    }`}
  >
    <GreyBoxLoader className={styles.loginTitle} />
    <div className={styles.loginFields}>
      <GreyBoxLoader className={styles.fieldInput} />
      <GreyBoxLoader className={styles.fieldInput} />
    </div>
    <GreyBoxLoader className={styles.action} />
    <GreyBoxLoader className={styles.link} />
    {showRegistration && (
      <GreyBoxLoader className={styles.loginRegistrationAction} />
    )}
    <div className={styles.loginFooter}>
      <div className={styles.country}>
        <GreyCircleLoader size={24} />
        <GreyBoxLoader className={styles.countryName} />
      </div>
      <GreyBoxLoader className={styles.footerLine} />
      <GreyBoxLoader className={styles.footerLinks} />
    </div>
  </div>
);

const AuthLoadingRegistration = () => (
  <div className={`${styles.card} ${styles.registrationCard}`}>
    <GreyBoxLoader className={styles.sectionTitle} />
    <div className={styles.country}>
      <GreyCircleLoader size={24} />
      <GreyBoxLoader className={styles.countryName} />
      <GreyBoxLoader className={styles.countryAction} />
    </div>
    <GreyBoxLoader className={styles.sectionTitle} />
    <div className={styles.registrationFields}>
      <GreyBoxLoader className={styles.fieldInput} />
      <GreyBoxLoader className={styles.fieldInput} />
      <GreyBoxLoader className={styles.fieldInput} />
      <GreyBoxLoader className={styles.fieldInput} />
    </div>
    <GreyBoxLoader className={styles.action} />
  </div>
);

const AuthLoadingPasswordReset = () => (
  <div className={`${styles.card} ${styles.passwordResetCard}`}>
    <div className={styles.question}>
      <GreyBoxLoader className={styles.questionLine} />
      <GreyBoxLoader className={styles.questionLineShort} />
    </div>
    <GreyBoxLoader className={styles.fieldInput} />
    <div className={styles.notice}>
      <GreyBoxLoader className={styles.noticeIcon} />
      <div className={styles.noticeCopy}>
        <GreyBoxLoader className={styles.noticeLine} />
        <GreyBoxLoader className={styles.noticeLineShort} />
      </div>
    </div>
    <div className={styles.support}>
      <GreyBoxLoader className={styles.supportTitle} />
      <GreyBoxLoader className={styles.supportLine} />
      <GreyBoxLoader className={styles.supportLine} />
      <GreyBoxLoader className={styles.supportLineShort} />
    </div>
    <GreyBoxLoader className={styles.action} />
  </div>
);

const AuthLoadingSmartSprayerLanding = () => (
  <div className={`${styles.card} ${styles.smartSprayerCard}`}>
    <GreyBoxLoader className={styles.smartSprayerImage} />
    <GreyBoxLoader className={styles.smartSprayerTitle} />
    <div className={styles.smartSprayerCopy}>
      <GreyBoxLoader className={styles.smartSprayerCopyLine} />
      <GreyBoxLoader className={styles.smartSprayerCopyLine} />
      <GreyBoxLoader className={styles.smartSprayerCopyLineShort} />
      <GreyBoxLoader className={styles.smartSprayerCopyLineShort} />
    </div>
    <GreyBoxLoader className={styles.action} />
  </div>
);

const AuthLoadingEmailVerified = () => (
  <div className={styles.emailVerifiedLayout}>
    <img
      className={styles.emailVerifiedLogo}
      src={withAppBasePath('/assets/auth/auth-logo-r.svg')}
      alt=""
    />
    <div className={styles.emailVerifiedContent}>
      <GreyBoxLoader className={styles.emailVerifiedTitle} />
      <GreyBoxLoader className={styles.emailVerifiedAction} />
    </div>
  </div>
);

const AuthLoadingAcceptTerms = () => (
  <div className={styles.termsLayout}>
    <div className={styles.termsHeader}>
      <IconLogo logo={'xarvio-3'} size={'small'} />
    </div>
    <div className={styles.termsContent}>
      <GreyBoxLoader className={styles.termsTitle} />
      <GreyBoxLoader className={styles.termsCopy} />
      <GreyBoxLoader className={styles.termsCopy} />
      <GreyBoxLoader className={styles.termsCopyShort} />
      <GreyBoxLoader className={styles.termsHeading} />
      <GreyBoxLoader className={styles.termsCopy} />
      <GreyBoxLoader className={styles.termsCopyShort} />
    </div>
    <div className={styles.termsFooter}>
      <div className={styles.termsAgreement}>
        <GreyBoxLoader className={styles.termsCheckbox} />
        <GreyBoxLoader className={styles.termsAgreementCopy} />
      </div>
      <GreyBoxLoader className={styles.termsAction} />
    </div>
  </div>
);

export const AuthRoutesLoading = () => {
  const { pathname, search } = useLocation();
  const { whitelabelConfig } = useWhitelabel();
  const isUserRegistrationEnabled = useIsFeatureEnabled('USER_REGISTRATION');
  const variant = getAuthLoadingVariant(pathname);
  const showLoginRegistration =
    isUserRegistrationEnabled && !isBarillaDomain() && !isPeroniDomain();
  const country = pathname.split('/')[1];
  const bgSrc = getAuthBackgroundSrc({
    pathname,
    search,
    whitelabelAuthBgSrc: whitelabelConfig?.authBgSrc,
  });

  if (variant === AuthLoadingVariant.AutoLogin) {
    return (
      <Overlay>
        <Loader />
      </Overlay>
    );
  }

  if (variant === AuthLoadingVariant.EmailVerified) {
    return <AuthLoadingEmailVerified />;
  }

  if (variant === AuthLoadingVariant.AcceptTerms) {
    return <AuthLoadingAcceptTerms />;
  }

  return (
    <IntlProvider
      locale={'en'}
      messages={AUTH_LOADING_MESSAGES}
      onError={ignoreIntlError}
    >
      <PageLayoutAuth
        country={country}
        bgSrc={bgSrc}
        hasLogo={variant === AuthLoadingVariant.Login}
        customLogoEl={
          variant === AuthLoadingVariant.SmartSprayerLanding ? (
            <GreyBoxLoader className={styles.smartSprayerLogo} />
          ) : undefined
        }
        title={
          variant !== AuthLoadingVariant.Login ? (
            <GreyBoxLoader className={styles.headerTitle} />
          ) : undefined
        }
      >
        {variant === AuthLoadingVariant.Login && (
          <AuthLoadingLogin showRegistration={showLoginRegistration} />
        )}
        {variant === AuthLoadingVariant.Registration && (
          <AuthLoadingRegistration />
        )}
        {variant === AuthLoadingVariant.PasswordReset && (
          <AuthLoadingPasswordReset />
        )}
        {variant === AuthLoadingVariant.SmartSprayerLanding && (
          <AuthLoadingSmartSprayerLanding />
        )}
      </PageLayoutAuth>
    </IntlProvider>
  );
};
